/**
 * Nira design-system components, ported from the system's bundle.js. Class
 * names and props match the system's index.d.ts, so its previews stay the
 * contract; ds.css carries the styles.
 */
import { useId, type ButtonHTMLAttributes, type FormEvent, type ReactNode } from "react"
import { cx } from "./cx"
import { Lean, LEAN_WORD, type LeanKind } from "./Beam"

/* ---------- Button ---------- */

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** primary = cobalt fill (the one action on a screen). ink = dark fill, only in dark-on-light contexts like share. secondary = outline (default). quiet = text on hover surface. link = cobalt words. */
  variant?: "primary" | "ink" | "secondary" | "quiet" | "link"
  size?: "sm"
  icon?: ReactNode
  href?: string
}

export function Button({ variant = "secondary", size, icon, href, children, className, type, ...rest }: ButtonProps) {
  const common = {
    className: cx("nr", "nr-btn", className),
    "data-variant": variant,
    "data-size": size,
    "data-icon": icon && children === undefined ? "only" : undefined,
  }
  const body = (
    <>
      {icon ?? null}
      {children !== undefined ? <span>{children}</span> : null}
    </>
  )
  if (href) {
    return (
      <a {...common} href={href} aria-label={rest["aria-label"]} title={rest.title}>
        {body}
      </a>
    )
  }
  return (
    <button {...rest} {...common} type={type ?? "button"}>
      {body}
    </button>
  )
}

/* ---------- Context chip ---------- */

export type Side = "taxpayer" | "department"

export interface ContextChipProps {
  state?: string
  side?: Side
  onClick?: () => void
  className?: string
}

/** Top-bar chip holding State and side: "Telangana · for a taxpayer ▾". The only place the side is set. */
export function ContextChip({ state = "Telangana", side = "taxpayer", onClick, className }: ContextChipProps) {
  const sideWord = side === "department" ? "for the department" : "for a taxpayer"
  return (
    <button
      type="button"
      className={cx("nr", "nr-chip", className)}
      data-side={side}
      onClick={onClick}
      aria-haspopup="menu"
      aria-label={`Context: ${state}, ${sideWord}. Change`}
    >
      <span>{state}</span>
      <span className="nr-chip-dot" aria-hidden="true">
        ·
      </span>
      <span>{sideWord}</span>
      <svg viewBox="0 0 12 12" aria-hidden="true">
        <path d="M2.5 4.5 6 8l3.5-3.5" />
      </svg>
    </button>
  )
}

/* ---------- Cite ---------- */

export interface CiteProps {
  n: number | string
  href?: string
  status?: "binds" | "against"
  title?: string
  children?: ReactNode
  className?: string
  onClick?: () => void
}

export function Cite({ n, href, status, title, children, className, onClick }: CiteProps) {
  const body = (
    <>
      {`[${String(n)}]`}
      {children ? <span>{children}</span> : null}
    </>
  )
  const props = { className: cx("nr", "nr-cite", className), "data-status": status, title }
  if (href) return <a {...props} href={href} onClick={onClick}>{body}</a>
  if (onClick) return <button type="button" {...props} onClick={onClick}>{body}</button>
  return <span {...props}>{body}</span>
}

/* ---------- Verdict ---------- */

export interface VerdictProps {
  eyebrow?: string
  verdict: string
  lead?: string
  meta?: ReactNode
  className?: string
}

export function Verdict({ eyebrow, verdict, lead, meta, className }: VerdictProps) {
  return (
    <div className={cx("nr", "nr-verdict", className)}>
      {eyebrow ? <div className="nr-verdict-eyebrow">{eyebrow}</div> : null}
      <h1 className="nr-verdict-text">{verdict}</h1>
      {lead ? <p className="nr-verdict-lead">{lead}</p> : null}
      {meta ? <div className="nr-verdict-meta">{meta}</div> : null}
    </div>
  )
}

/* ---------- Glance ---------- */

export interface GlanceItem {
  label: string
  value: ReactNode
  sub?: string
  lean?: LeanKind
  mono?: boolean
}

export function Glance({ items, className }: { items: GlanceItem[]; className?: string }) {
  return (
    <div className={cx("nr", "nr-glance", className)} role="list" aria-label="At a glance">
      {items.map((it, i) => (
        <div className="nr-glance-cell" role="listitem" key={i}>
          <div className="nr-glance-label">{it.label}</div>
          <div className="nr-glance-value">
            {it.lean ? <Lean lean={it.lean} label={null} /> : null}
            {it.mono ? <span className="nr-mono">{it.value}</span> : it.value}
          </div>
          {it.sub ? <div className="nr-glance-sub">{it.sub}</div> : null}
        </div>
      ))}
    </div>
  )
}

/* ---------- Decision row ---------- */

export interface DecisionRowProps {
  lean?: LeanKind
  name: string
  court?: string
  scope?: string
  hold?: string
  href?: string
  className?: string
}

export function DecisionRow({ lean, name, court, scope, hold, href, className }: DecisionRowProps) {
  const body = (
    <>
      <div className="nr-decision-name">
        {lean ? <Lean lean={lean} label={null} title={LEAN_WORD[lean]} /> : null}
        {lean ? " " : null}
        {name}
      </div>
      {court ? <div className="nr-decision-court">{court}</div> : null}
      {scope ? <div className="nr-decision-scope">{scope}</div> : null}
      {hold ? <div className="nr-decision-hold">{hold}</div> : null}
    </>
  )
  return href ? (
    <a className={cx("nr", "nr-decision", className)} href={href}>
      {body}
    </a>
  ) : (
    <div className={cx("nr", "nr-decision", className)}>{body}</div>
  )
}

export interface DecisionGroupProps {
  title: string
  lean: LeanKind
  count?: number
  aside?: string
  moreHref?: string
  moreLabel?: string
  children?: ReactNode
  className?: string
}

DecisionRow.Group = function DecisionGroup({ title, lean, count, aside, moreHref, moreLabel, children, className }: DecisionGroupProps) {
  return (
    <section className={cx("nr", "nr-decisions", className)} aria-label={title}>
      <div className="nr-decisions-head">
        <Lean lean={lean} label={title} size="lg" count={count} />
        {aside ? (
          <span className="nr-mono" style={{ fontSize: "11.5px", color: "var(--ink-3)" }}>
            {aside}
          </span>
        ) : null}
      </div>
      {children}
      {moreHref ? (
        <a className="nr-decisions-more" href={moreHref}>
          {moreLabel || "More decisions, ranked"}
          <span aria-hidden="true">→</span>
        </a>
      ) : null}
    </section>
  )
}

/* ---------- Reading plan ---------- */

export interface ReadingPlanItem {
  num: string
  title: string
  time?: string
  href?: string
  current?: boolean
}
export interface ReadingPlanGroup {
  label: string
  tone?: "for"
  items: ReadingPlanItem[]
}

export function ReadingPlan({ groups, label, className }: { groups: ReadingPlanGroup[]; label?: string; className?: string }) {
  return (
    <nav className={cx("nr", "nr-plan", className)} aria-label={label || "Reading plan"}>
      {groups.map((g, gi) => (
        <div className="nr-plan-group" key={gi}>
          <div className="nr-plan-label" data-tone={g.tone}>
            {g.label}
          </div>
          {g.items.map((it, i) => (
            <a className="nr-plan-item" href={it.href || "#"} key={i} aria-current={it.current ? "true" : undefined}>
              <span className="nr-plan-num">{it.num}</span>
              <span className="nr-plan-title">{it.title}</span>
              <span className="nr-plan-time">{it.time || ""}</span>
            </a>
          ))}
        </div>
      ))}
    </nav>
  )
}

/* ---------- Exposure ---------- */

export interface ExposureRow {
  label: string
  basis?: ReactNode
  amount: string
}
export interface ExposureProps {
  rows: ExposureRow[]
  total?: ExposureRow
  note?: string
  headLabel?: string
  headBasis?: string
  headAmount?: string
  className?: string
}

export function Exposure({ rows, total, note, headLabel, headBasis, headAmount, className }: ExposureProps) {
  return (
    <div className={cx("nr", className)}>
      <table className="nr-exposure">
        <thead>
          <tr>
            <th>{headLabel || "If the demand stands"}</th>
            <th>{headBasis || "Basis"}</th>
            <th>{headAmount || "Amount"}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}>
              <td>{r.label}</td>
              <td>{r.basis}</td>
              <td>
                <span className="nr-mono">{r.amount}</span>
              </td>
            </tr>
          ))}
          {total ? (
            <tr data-total="true">
              <td>{total.label}</td>
              <td>{total.basis || ""}</td>
              <td>
                <span className="nr-mono">{total.amount}</span>
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
      {note ? <div className="nr-exposure-note">{note}</div> : null}
    </div>
  )
}

/* ---------- Deadline ---------- */

export interface DeadlineProps {
  label?: string
  date: string
  basis?: ReactNode
  set?: string
  children?: ReactNode
  className?: string
}

export function Deadline({ label, date, basis, set, children, className }: DeadlineProps) {
  return (
    <div className={cx("nr", "nr-deadline", className)} role="group" aria-label={label || "Deadline"}>
      <div className="nr-deadline-label">{label || "Reply by"}</div>
      <div className="nr-deadline-date">{date}</div>
      {basis ? <div className="nr-deadline-basis">{basis}</div> : null}
      {children ? <div className="nr-deadline-actions">{children}</div> : null}
      {set ? <div className="nr-deadline-set">{set}</div> : null}
    </div>
  )
}

/* ---------- Limits ---------- */

export interface LimitsProps {
  title?: string
  items: string[]
  foot?: string
  className?: string
}

export function Limits({ title, items, foot, className }: LimitsProps) {
  const heading = title || "What this does not cover"
  return (
    <section className={cx("nr", "nr-limits", className)} aria-label={heading}>
      <div className="nr-limits-title">{heading}</div>
      <ul>
        {items.map((t, i) => (
          <li key={i}>{t}</li>
        ))}
      </ul>
      <div className="nr-limits-foot">{foot || "Research, not legal advice. Check the cited text before you rely on it."}</div>
    </section>
  )
}

/* ---------- Door ---------- */

export interface DoorProps {
  /** rest = surface card; on = cobalt tint and border; soon = dashed, foot reads "Coming soon · Notify me". */
  state?: "rest" | "on" | "soon"
  num?: string
  title: string
  desc?: string
  foot?: string
  icon?: ReactNode
  href?: string
  onClick?: () => void
  className?: string
}

export function Door({ state = "rest", num, title, desc, foot, icon, href, onClick, className }: DoorProps) {
  const body = (
    <>
      <div className="nr-door-top">
        {icon ?? <span />}
        {num ? <span className="nr-door-num">{num}</span> : null}
      </div>
      <div className="nr-door-title">{title}</div>
      {desc ? <div className="nr-door-desc">{desc}</div> : null}
      {foot ? <div className="nr-door-foot">{foot}</div> : null}
    </>
  )
  const props = {
    className: cx("nr", "nr-door", className),
    "data-state": state,
    onClick,
    "aria-current": state === "on" ? ("true" as const) : undefined,
  }
  return href ? (
    <a {...props} href={href}>
      {body}
    </a>
  ) : (
    <button type="button" {...props}>
      {body}
    </button>
  )
}

Door.Grid = function DoorGrid({ children, className }: { children?: ReactNode; className?: string }) {
  return <div className={cx("nr", "nr-doors", className)}>{children}</div>
}

/* ---------- Composer ---------- */

export interface ComposerProps {
  id?: string
  placeholder?: string
  value?: string
  hint?: string
  aside?: ReactNode
  attachLabel?: string
  submitLabel?: string
  ariaLabel?: string
  onSubmit?: (e: FormEvent) => void
  className?: string
}

const ClipIcon = (
  <svg viewBox="0 0 24 24">
    <path d="M21 11.5 12.5 20a5 5 0 0 1-7-7l8.5-8.5a3.3 3.3 0 0 1 4.7 4.7L10.3 17.6a1.6 1.6 0 0 1-2.3-2.3L16 7.3" />
  </svg>
)
const ArrowIcon = (
  <svg viewBox="0 0 24 24">
    <path d="M12 19V5M6 11l6-6 6 6" />
  </svg>
)

export function Composer({ id, placeholder, value, hint, aside, attachLabel, submitLabel, ariaLabel, onSubmit, className }: ComposerProps) {
  return (
    <form
      className={cx("nr", "nr-composer", className)}
      onSubmit={(e) => {
        e.preventDefault()
        onSubmit?.(e)
      }}
    >
      <div className="nr-composer-field">
        <textarea
          className="nr-composer-input"
          id={id || "nr-composer"}
          rows={1}
          placeholder={placeholder || "Describe your situation, or drop a notice here."}
          defaultValue={value}
          aria-label={ariaLabel || "Describe your situation"}
        />
        <Button variant="quiet" icon={ClipIcon} aria-label={attachLabel || "Attach a notice or draft"} />
        <Button variant="primary" type="submit" icon={ArrowIcon} aria-label={submitLabel || "Ask"} />
      </div>
      <div className="nr-composer-hint">
        <span>{hint || "Attach a notice or draft · PDF, Word, or a phone photo"}</span>
        {aside ? <span>{aside}</span> : null}
      </div>
    </form>
  )
}

/* ---------- Overlay ---------- */

export interface OverlayProps {
  title: string
  body?: string
  foot?: ReactNode
  children?: ReactNode
  className?: string
}

export function Overlay({ title, body, foot, children, className }: OverlayProps) {
  const titleId = useId()
  return (
    <div className={cx("nr", "nr-overlay-backdrop", className)}>
      <div className="nr-overlay" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <h2 className="nr-overlay-title" id={titleId}>
          {title}
        </h2>
        {body ? <p className="nr-overlay-body">{body}</p> : null}
        {children}
        {foot ? <div className="nr-overlay-foot">{foot}</div> : null}
      </div>
    </div>
  )
}

/* ---------- Masked ---------- */

export interface MaskedProps {
  kind?: "name" | "gstin" | "amount"
  title?: string
  children?: ReactNode
  className?: string
}

export function Masked({ kind = "name", title, children, className }: MaskedProps) {
  const text = children || (kind === "gstin" ? "36•••••••••••1ZS" : kind === "amount" ? "₹••,••,•••" : "•••• •••••")
  return (
    <span className={cx("nr", "nr-masked", className)} data-kind={kind} title={title || "Masked before anything left your device"}>
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <rect x={5} y={10} width={14} height={10} rx={2} />
        <path d="M8 10V7a4 4 0 0 1 8 0v3" />
      </svg>
      <span>{text}</span>
    </span>
  )
}
